/** The steps a filing goes through, said from what the server sent - pure, so it is tested without a
 * DOM (v5 stage F3).
 *
 * **No percentage, and no step the product cannot show.** The checks read gives four named parts
 * (data in, figures verified, approvals, disclosures complete) with passed/total counts; the verdict
 * gives the last two rungs of F07's ladder. A step is `done` only when the server says so; a check
 * nobody ran is `unknown`, never a pass (a filing is not ready until its checks have run), and a
 * step that waits on another is `ahead`. "Compare with last year" is deliberately **not** a step:
 * the product records no act of having compared, so a tick there would be invented.
 *
 * **`checks_passed` is not approval** (F07). On the verdict-only spine the approval step is
 * `current` ("waiting") at that rung and `done` only at `approved`, which has a person behind it.
 */

import type { FilingCheckPart } from "../api/filingChecks";
import type { FilingStateRead } from "./filingState";

export type StepState = "done" | "current" | "blocked" | "ahead" | "unknown" | "na";

export interface SpineStep {
  key: string;
  label: string;
  state: StepState;
  /** A few words under the label: the counts or the reason, never a sentence. */
  note: string;
  /** The station the filing is at: **the first one that is not done** (and not "not applicable"),
   * whatever state it is in. A blocked station is where the work is as much as an in-progress one,
   * and the path used to draw a later advisory station as the large current one while Data in was
   * blocked (design review 2026-11-03). Set only by `stepsFromChecks`. */
  here?: true;
}

/** The words a state is read aloud as; the glyph is decoration. */
export const STATE_WORD: Record<StepState, string> = {
  done: "Done",
  current: "In progress",
  blocked: "Blocked",
  ahead: "Not yet",
  unknown: "Not checked",
  na: "Not applicable",
};

const CHECKS_DONE = new Set(["checks_passed", "approved", "issued"]);
const KNOWN = new Set([
  "not_evaluated",
  "unsupported",
  "incomplete",
  "checks_passed",
  "approved",
  "issued",
]);

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function issuedStep(verdict: string | undefined, standing?: FilingStateRead): SpineStep {
  // Something was issued, whatever the checks say now: the station is done, and the version says which
  // (a filing issued twice read "Not yet" here while Past filings listed both).
  if (standing && standing.issued_versions > 0) {
    const note = standing.issued_versions > 1 ? `v${String(standing.issued_versions)} on record` : "On record";
    return { key: "issued", label: "Issued", state: "done", note };
  }
  if (verdict === "issued") return { key: "issued", label: "Issued", state: "done", note: "On record" };
  if (verdict === "approved") {
    return { key: "issued", label: "Issued", state: "current", note: "Ready to issue" };
  }
  return { key: "issued", label: "Issued", state: "ahead", note: "After approval" };
}

/** Three steps from the verdict alone: a row of the filings list has the report and no checks read. */
export function stepsFromVerdict(
  verdict: string | undefined,
  blocking: number,
  standing?: FilingStateRead,
): SpineStep[] {
  if (verdict === undefined || !KNOWN.has(verdict)) {
    return [
      { key: "checks", label: "Checks", state: "unknown", note: "Not read" },
      { key: "approval", label: "Approval", state: "unknown", note: "Not read" },
      { key: "issued", label: "Issued", state: "unknown", note: "Not read" },
    ];
  }
  const checks: SpineStep = CHECKS_DONE.has(verdict)
    ? { key: "checks", label: "Checks", state: "done", note: "Passed" }
    : verdict === "incomplete"
      ? {
          key: "checks",
          label: "Checks",
          state: "blocked",
          note: blocking > 0 ? plural(blocking, "blocking", "blocking") : "Not ready",
        }
      : verdict === "unsupported"
        ? { key: "checks", label: "Checks", state: "blocked", note: "Not supported" }
        : { key: "checks", label: "Checks", state: "unknown", note: "Not run" };
  // An issued filing's approval is done only if one is in force (G-Wauditor-02). Home ticked "Approval,
  // Done" for every issued filing, including one whose approval lapsed when its period was reopened and a
  // figure corrected, while the filing page said no approval was in force.
  const lapsed = standing !== undefined && standing.issued_versions > 0 && !standing.approval_in_force;
  const approval: SpineStep = lapsed
    ? {
        key: "approval",
        label: "Approval",
        state: verdict === "incomplete" ? "ahead" : "current",
        note: verdict === "incomplete" ? "After checks" : "Needed",
      }
    : verdict === "approved" || verdict === "issued"
      ? { key: "approval", label: "Approval", state: "done", note: "Approved" }
      : verdict === "checks_passed"
        ? { key: "approval", label: "Approval", state: "current", note: "Waiting" }
        : { key: "approval", label: "Approval", state: "ahead", note: "After checks" };
  return [checks, approval, issuedStep(verdict, standing)];
}

function stepOfPart(part: FilingCheckPart): SpineStep {
  const counts = `${part.passed} of ${part.total}`;
  switch (part.state) {
    case "passed":
      return { key: part.part, label: part.label, state: "done", note: counts };
    case "blocking":
      return { key: part.part, label: part.label, state: "blocked", note: counts };
    case "advisory":
      return { key: part.part, label: part.label, state: "current", note: `${counts}, advisory` };
    case "waiting":
      return { key: part.part, label: part.label, state: "ahead", note: "Waiting" };
    case "not_evaluated":
      return { key: part.part, label: part.label, state: "unknown", note: "Not run" };
    case "not_applicable":
      return { key: part.part, label: part.label, state: "na", note: "None in this regime" };
    default:
      return { key: part.part, label: part.label, state: "unknown", note: "Not recognised" };
  }
}

/** The filing's own spine: the server's four parts, in the server's order, then the issue step.
 *
 * **The current station is the first one that is not done.** That one is marked `here`; it becomes
 * `current` when the server's state for it is a wait, and stays `blocked` or `unknown` when it is
 * one of those (the glyph and the word say which). A later station the server calls `current` (an
 * advisory note on Disclosures) is not where the filing is, so it reads `ahead` and keeps its note:
 * two "current" stations, one of them behind a blocked one, is how the path once pointed at the
 * wrong place. */
export function stepsFromChecks(
  parts: readonly FilingCheckPart[],
  verdict: string | undefined,
  standing?: FilingStateRead,
): SpineStep[] {
  const steps = [...parts.map(stepOfPart), issuedStep(verdict, standing)];
  const at = steps.findIndex((step) => step.state !== "done" && step.state !== "na");
  return steps.map((step, index): SpineStep => {
    if (index === at) {
      return { ...step, state: step.state === "ahead" ? "current" : step.state, here: true };
    }
    if (index > at && step.state === "current") return { ...step, state: "ahead" };
    return step;
  });
}

/** The words a station is in, lower case, for the sentence beside the compact path's dots. */
const IN_WORDS: Record<StepState, string> = {
  done: "done",
  current: "in progress",
  blocked: "blocked",
  ahead: "waiting",
  unknown: "not checked",
  na: "not applicable",
};

/** What the compact path says in words: the station the filing is at and how far along it is ("Data
 * in, blocked · 2 of 5 done"), or the last station when every one is done. */
export function stepperText(steps: readonly SpineStep[], terse = false): string {
  if (steps.length === 0) return "Not read";
  const here = steps.find((step) => step.here) ?? steps.find((step) => step.state !== "done" && step.state !== "na");
  const { done, of } = doneCount(steps);
  // `terse` is for a strip with a words budget (Home): the station and its state, and the count is
  // left to the tooltip and the list a screen reader hears.
  if (!here) return terse ? (steps[steps.length - 1]?.label ?? "Done") : `${steps[steps.length - 1]?.label ?? "Done"} · ${String(done)} of ${String(of)} done`;
  return terse ? `${here.label}, ${IN_WORDS[here.state]}` : `${here.label}, ${IN_WORDS[here.state]} · ${String(done)} of ${String(of)} done`;
}

/** How many of the stations that apply are done: "2 of 5". */
export function doneCount(steps: readonly SpineStep[]): { done: number; of: number } {
  const counted = steps.filter((step) => step.state !== "na");
  return { done: counted.filter((step) => step.state === "done").length, of: counted.length };
}

/** The step to say first: the first one that is not done and not applicable. */
export function currentStep(steps: readonly SpineStep[]): SpineStep | undefined {
  return steps.find((step) => step.state !== "done" && step.state !== "na");
}
